import {Events} from "../minicart/CartEvents";
import {addTouch} from "../touch/Touch";
import Overlay from "../Overlay";
import {Helpers} from "../../../common/js/Helpers";
import {icon} from "../../../common/js/icon";
import {pickFromSrcSet} from "../../../common/js/srcset";

    /*
     *    Constructor. Starts up the whole thing :-)
     *
     *    This script is free to use. It has been created by http://www.aplusmedia.de and
     *    can be downloaded on http://www.esteak.net.
     *    License: GNU GPL 2.0: http://creativecommons.org/licenses/GPL/2.0/
     *    Example on: http://blog.aplusmedia.de/moo-gallery2
     *    Known issues:
     *    - preloading does not care about initialIndex param
     *    - hovering to a control over the border of the big image will make the other one flickering
     *    - if you enter and leave the control area very quickly, the control flickers sometimes
     *
     *    @param {HTMLElement} galleryElement The main gallery element
     *    @param {Array} thumbs, An array of HTML elements
     *    @param {HTMLElement} bigImageContainer, the full size image
     *    @param {HTMLElement} pageContainer, If you have several pages, put them in this container
     *    @param {Object} newOptions, You have to pass imagesPerPage if you have more than one!
     */
export class JSGallery2 {
    constructor(galleryElement, thumbs, bigImageContainer, pageContainer, newOptions) {
        const defaults = {
            'prevHandle': null,			//if you pass a previous page handle in here, it will be hidden if it's not needed
            'nextHandle': null,			//like above, but for next page
            'countHandle': null,		//handle of the counter variable
            'titleTarget': null,		//target HTML element where image texts are copied into
            'initialIndex': -1,			//which thumb to select after init. you could create deep links with it.
            'showSocialMediaButton': true,
            'showCartButton': true,
            'showCartConnector': false,
            'cartConnectorLinkRel': '',
            'activeClass': 'thumbnail-active', // the css class for the active thumbnail
            'touchContainerSelector' : '#bigimageContainer',
            'maxHeight': 280			//the maximum height in px one page of thumbnails may use
        };

        this.options = {...defaults, ...newOptions};

        this.galleryElement = galleryElement;
        this.nav = galleryElement.querySelector('.navigation');
        this.image = galleryElement.querySelector('.image');

        // one controller for every listener this instance registers, so destroy()
        // can detach all of them at once.
        this.abortController = new AbortController();

        // The index of the selected thumbnail is the single source of truth of
        // this gallery - the DOM (active class, big image, url, page offset) is
        // derived from it. -1 means "nothing selected yet".
        this.currentIndex = -1;
        this.currentPageNumber = -1;

        // How the thumbnails are spread over the pages is not configured but
        // measured, see distribute(). Until that ran, everything sits on one page.
        this.imagesPerFirstPage = 0;
        this.imagesPerPage = 0;
        this.lastPage = 1;

        this.thumbs = Array.from(thumbs);
        this.bigImageContainer = bigImageContainer;
        this.bigImage = bigImageContainer.querySelector('img');
        this.pageContainer = pageContainer;

        // the intro content of the first page, if the template renders it inline
        this.introElement = pageContainer.querySelector('.information-inline');

        // the <li> elements of the paging bar, created lazily by updatePagingBar()
        this.countLinks = [];

        this.convertThumbs();

        let url = document.location.href;
        this.initialIndex = parseInt(Helpers.getUrlHashParameterValue(url, 'imageno'));
        if (isNaN(this.initialIndex)) {
            this.initialIndex = 0;
        }

        this.createControls();

        this.createGallerySlides();
        this.adjustImageSize();
        this.distribute();

        this.gotoPage(0);

        if (this.options.initialIndex !== -1) {
            this.selectByIndex(this.options.initialIndex);
        } else if (this.initialIndex !== 0) {
            this.selectByIndex(this.initialIndex);
        }

        // everything is in place, the navigation may show itself
        this.nav.classList.add('is-ready');

        const signal = this.abortController.signal;
        window.addEventListener('load', () => {this.resizePage()}, {signal});
        window.addEventListener('resize', () => {this.resizePage()}, {signal});
        window.addEventListener('popstate', () => {this.selectFromUrl()}, {signal});

        this.observeLayoutChanges();
    }

    /**
     * The container of the currently selected thumbnail, derived from currentIndex.
     * @return {HTMLElement|undefined}
     */
    get selectedContainer() {
        return this.currentIndex === -1 ? undefined : this.thumbs[this.currentIndex];
    }

    /**
     * Measures the outer size of a rendered thumbnail and publishes it as a
     * custom property, so the stylesheet can build columns of exactly that
     * width. The template only knows the image size - the theme adds its own
     * padding and border on top - so this has to happen in the browser.
     *
     * @return {{width: Number, height: Number}} the outer size of one thumbnail
     */
    measureThumbSize() {
        const item = this.thumbs[0];
        const link = item.querySelector('a.event-thumbnail') ?? item;

        // Take the thumbnails out of the grid for the moment of the measurement.
        // Inside it their width is capped to the very column this measurement is
        // supposed to size (themes put max-width: 100% on the thumbnails), so
        // measuring there would only ever report the current column width back -
        // a stable, but wrong answer which no later measurement could correct.
        this.galleryElement.classList.add('is-measuring');

        // The width comes from the link, an inline-block sized by its content.
        // The height comes from the grid item, because that is what occupies a row.
        const size = {
            width: Math.round(link.getBoundingClientRect().width),
            height: Math.round(item.getBoundingClientRect().height)
        };

        this.galleryElement.classList.remove('is-measuring');

        if (size.width > 0) {
            this.galleryElement.style.setProperty('--eg-thumb-size', size.width + 'px');
        }

        return size;
    }

    /**
     * How much vertical space is used up before the thumbnails of the first page
     * start - the intro content plus whatever margins sit between it and the
     * grid.
     *
     * This measures the distance between the top of the page and the top of the
     * grid rather than the height of the intro element, because a margin of the
     * intro's last child collapses out of it and would otherwise be missed,
     * which made pages come out taller than the configured maximum. The room
     * the stylesheet puts above the grid of a page shorter than the tallest one
     * (its margin-top: auto, ajax.less) is no intro and does not count.
     *
     * @param {HTMLElement} grid the .event-thumbnails of the first page
     * @return {Number}
     */
    measureIntroHeight(grid) {
        const page = grid.parentElement;
        if (page === null || !this.introElement) {
            return 0;
        }

        const room = parseFloat(window.getComputedStyle(grid).marginTop) || 0;

        return Math.max(0, grid.getBoundingClientRect().top - page.getBoundingClientRect().top - room);
    }

    /**
     * Works out how many thumbnails fit on a page at the current window width.
     *
     * The number of columns is not calculated but read back from the resolved
     * grid, so the stylesheet stays the single place that decides how the
     * thumbnails are laid out. The number of rows follows from the configured
     * maximum height, where the first page has to share that height with the
     * intro content.
     *
     * @return {{perFirstPage: Number, perPage: Number}}
     */
    measureCapacity() {
        const grid = this.pageContainer.querySelector('.event-thumbnails');
        if (grid === null) {
            return {perFirstPage: this.thumbs.length, perPage: this.thumbs.length};
        }

        const thumbSize = this.measureThumbSize();
        const style = window.getComputedStyle(grid);
        const rowGap = parseFloat(style.rowGap) || 0;

        // "80px 80px 80px" -> 3 columns. An empty grid reports "none".
        const tracks = style.gridTemplateColumns;
        let columns = tracks && tracks !== 'none' ? tracks.split(' ').filter((t) => t !== '').length : 0;

        if (columns < 1) {
            columns = 1;
        }

        const rowHeight = thumbSize.height + rowGap;
        const rowsFor = (available) => rowHeight > 0 ? Math.max(1, Math.floor((available + rowGap) / rowHeight)) : 1;

        const maxHeight = this.options.maxHeight;
        const introHeight = this.measureIntroHeight(grid);

        return {
            perFirstPage: columns * rowsFor(maxHeight - introHeight),
            perPage: columns * rowsFor(maxHeight)
        };
    }

    /**
     * Spreads the thumbnails over as many pages as the current window width
     * needs and rebuilds the paging bar for the new number of pages.
     *
     * Does nothing when the capacity did not change, so a resize that does not
     * cross a column or row boundary is free.
     *
     * @param {Boolean} [force] rebuild even if the capacity is unchanged
     * @return {Boolean} whether the pages were rebuilt
     */
    distribute(force = false) {
        if (this.thumbs.length === 0) {
            return false;
        }

        const capacity = this.measureCapacity();

        if (!force
            && capacity.perFirstPage === this.imagesPerFirstPage
            && capacity.perPage === this.imagesPerPage) {
            return false;
        }

        this.imagesPerFirstPage = capacity.perFirstPage;
        this.imagesPerPage = capacity.perPage;
        this.lastPage = this.pageForIndex(this.thumbs.length - 1) + 1;

        this.buildPages();
        this.resetPagingBar();

        // the current page number refers to the old distribution, so force
        // showPage() to apply the page of the selected image again
        this.currentPageNumber = -1;

        return true;
    }

    /**
     * (Re)creates the page elements and moves the thumbnails into them.
     */
    buildPages() {
        const pages = [];

        for (let pageNumber = 0; pageNumber < this.lastPage; pageNumber++) {
            const page = document.createElement('div');
            page.classList.add('page');

            if (pageNumber === 0 && this.introElement) {
                page.appendChild(this.introElement);
            }

            const thumbnails = document.createElement('div');
            thumbnails.classList.add('event-thumbnails');

            const from = this.firstIndexOfPage(pageNumber);
            const to = Math.min(from + (pageNumber === 0 ? this.imagesPerFirstPage : this.imagesPerPage), this.thumbs.length);

            for (let i = from; i < to; i++) {
                thumbnails.appendChild(this.thumbs[i]);
            }

            page.appendChild(thumbnails);
            pages.push(page);
        }

        // the thumbnails are already detached from the old pages by appendChild
        this.pageContainer.replaceChildren(...pages);
    }

    /**
     * Throws the paging bar away so updateHandles() builds a fresh one for the
     * current number of pages.
     */
    resetPagingBar() {
        this.countLinks.forEach((element) => element.remove());
        this.countLinks = [];
    }

    /**
     * The page a thumbnail lives on.
     * @param {Number} index of the thumbnail, starting with 0
     * @return {Number} page number, starting with 0
     */
    pageForIndex(index) {
        if (index < this.imagesPerFirstPage) {
            return 0;
        }
        return Math.floor((index - this.imagesPerFirstPage) / this.imagesPerPage) + 1;
    }

    /**
     * The index of the first thumbnail of a page.
     * @param {Number} pageNumber starting with 0
     * @return {Number}
     */
    firstIndexOfPage(pageNumber) {
        if (pageNumber <= 0) {
            return 0;
        }
        return (pageNumber - 1) * this.imagesPerPage + this.imagesPerFirstPage;
    }

    /**
     * Detaches every listener this instance registered and removes the controls
     * it injected into the markup.
     */
    destroy() {
        this.abortController.abort();
        window.clearTimeout(this.eventgalleryAjaxResizeTimer);
        this.resizeObserver?.disconnect();

        [
            this.prevLink,
            this.zoomLink,
            this.nextLink,
            this.add2cartLink,
            this.cartConnectorLink,
            this.socialmediabutton,
            this.bigImageBackdrop
        ].forEach((element) => element?.remove());

        this.resetPagingBar();
        this.nav.classList.remove('is-ready');
        this.bigImageContainer.classList.remove('is-video');

        if (this.selectedContainer) {
            this.deselect(this.selectedContainer);
        }
        this.currentIndex = -1;
    }

    /*
     * Writes the file of the stage into the rel attribute of every thumbnail: the candidate
     * of the lightbox srcset (data-eg-srcset) which fills the stage in device pixels, else
     * the large image (href). Until 7.0.0 the viewer rebuilt the address of the storage from
     * the rel attribute and from Flickr secrets, in CSS pixels.
     */
    adjustImageSize() {
        const stageWidth = Helpers.getElementWidth(this.bigImageContainer);
        const stageHeight = Helpers.getElementHeight(this.bigImageContainer);

        this.nav.querySelectorAll('#thumbs .event-thumbnail').forEach((thumbnail) =>  {

            let originalwidth = thumbnail.getAttribute('data-width');
            let originalheight = thumbnail.getAttribute('data-height');
            let     ratio = originalwidth / originalheight;

            // The stage shows the image with object-fit: contain, so it is
            // scaled down until it fits in *both* directions. Asking for the
            // full stage width would fetch a needlessly large file for
            // everything taller than the stage, which is most portrait images.
            let width = stageWidth;
            let height = Math.round(stageWidth / ratio);

            if (stageHeight > 0 && height > stageHeight) {
                height = Math.round(stageHeight);
                width = Math.round(stageHeight * ratio);
            }

            const imageUrl = pickFromSrcSet(thumbnail.getAttribute('data-eg-srcset'), width)
                || thumbnail.getAttribute('data-src')
                || thumbnail.getAttribute('href');
            thumbnail.setAttribute('rel', imageUrl);
        });
    }

    /*
     * Method which handles the case the window got resized. The layout of a
     * single page is done by CSS, but how many thumbnails fit on a page depends
     * on the window width, so they may have to be spread over a different
     * number of pages now.
     */
    resizePage() {
        window.clearTimeout(this.eventgalleryAjaxResizeTimer);

        this.eventgalleryAjaxResizeTimer = setTimeout(() => this.refreshLayout(), 500);
    }

    /**
     * Re-measures everything that depends on the size of the gallery and applies
     * the result. Cheap when nothing changed, because distribute() compares the
     * capacity before it touches the DOM.
     */
    refreshLayout() {
        this.adjustImageSize();
        this.distribute();
        // adjustImageSize() rewrote the image urls, so the big image has to
        // be loaded again - the selection itself does not change.
        this.resetThumbs();
        this.showPage(this.pageForIndex(this.currentIndex));
    }

    /**
     * Watches for changes which alter how many thumbnails fit on a page but do
     * not come with a window resize:
     *
     * - a web font arriving late changes the height of the intro content, and the
     *   first visitor of a site measures before that happened
     * - the gallery may sit in a container that changes width on its own, e.g. a
     *   tab or an accordion
     */
    observeLayoutChanges() {
        document.fonts?.ready?.then(() => this.refreshLayout()).catch(() => {});

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        this.lastObservedWidth = this.nav.getBoundingClientRect().width;
        this.resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0].contentRect.width;

            // Only react to width changes. Redistributing changes the height of
            // the observed element, so reacting to height would loop.
            if (width === this.lastObservedWidth) {
                return;
            }

            this.lastObservedWidth = width;
            this.resizePage();
        });
        this.resizeObserver.observe(this.nav);
    }


    /*
     * The slides of the lightbox, one per thumbnail: the large image and the sizes the
     * lightbox may pick from (data-eg-srcset), named like the thumbnail (its alt).
     */
    createGallerySlides() {
        this.slides = this.thumbs.map((thumbContainer) => {
            let lightboxLinkElement = thumbContainer.getElementsByTagName('A')[0];
            let title =  decodeURIComponent(lightboxLinkElement.getAttribute('data-description'));
            let slide =    {
                w: lightboxLinkElement.getAttribute('data-width'),
                h: lightboxLinkElement.getAttribute('data-height'),
                src: lightboxLinkElement.getAttribute('href'),
                srcset: lightboxLinkElement.getAttribute('data-eg-srcset') || '',
                alt: JSGallery2.altOf(lightboxLinkElement),
                title: title,
                type: lightboxLinkElement.getAttribute('data-type')
            };
            if (slide.type === 'video') {
                if (lightboxLinkElement.dataset.pswpVideoSources) {
                    slide.videoSources = JSON.parse(lightboxLinkElement.dataset.pswpVideoSources);
                } else if (lightboxLinkElement.dataset.pswpVideoSrc) {
                    slide.videoSrc = lightboxLinkElement.dataset.pswpVideoSrc;
                } else {
                    slide.videoSrc = lightboxLinkElement.getAttribute('href');
                }
            }
            return slide;
        });
    }

    createControls() {
        const signal = this.abortController.signal;

        // Fills whatever space object-fit: contain leaves next to the big image
        // with a blurred copy of it. Prepended so it stays behind everything else.
        this.bigImageBackdrop = document.createElement('img');
        this.bigImageBackdrop.id = 'bigImageBackdrop';
        this.bigImageBackdrop.alt = '';
        this.bigImageBackdrop.setAttribute('aria-hidden', 'true');
        this.bigImageContainer.prepend(this.bigImageBackdrop);

        // The controls of the stage are buttons with a name (the cart connector, which leads to
        // another shop, a link): the previous and the next zone and the zoom over the image, which
        // the stylesheet shows on hover and focus (ajax.less), and the chips of the cart, the cart
        // connector and the share dialog in the corners. Until 7.0.0 they were empty links to
        // "#" a screen reader announced as "link" (A11Y-7).
        // Each zone says what it does with an icon of the sprite in its middle; until 7.0.0 the
        // stylesheet put a graphic with the English word PREV, ZOOM or NEXT there.
        this.prevLink = JSGallery2.createControl('button', ['link', 'jsgallery-prev'], JSGallery2.label('KEY_PREVIOUS', 'Previous'));
        this.prevLink.innerHTML = JSGallery2.zoneMark('chevron-left');
        this.prevLink.addEventListener('click', (e)=> this.prevImage(e), {signal});

        this.zoomLink = JSGallery2.createControl('button', ['link', 'jsgallery-zoom'], JSGallery2.label('KEY_ZOOM_IMAGE', 'Zoom'));
        this.zoomLink.innerHTML = JSGallery2.zoneMark('zoom');
        this.zoomLink.addEventListener('click', (e) => { e.preventDefault(); this.bigImageClickHandler(); }, {signal});

        this.nextLink = JSGallery2.createControl('button', ['link', 'jsgallery-next'], JSGallery2.label('KEY_NEXT', 'Next'));
        this.nextLink.innerHTML = JSGallery2.zoneMark('chevron-right');
        this.nextLink.addEventListener('click', (e) => this.nextImage(e), {signal});

		this.bigImage.parentElement.appendChild(this.prevLink);
        this.bigImage.parentElement.appendChild(this.zoomLink);
        this.bigImage.parentElement.appendChild(this.nextLink);

        this.bigImage.addEventListener('load', ()=>this.showBigImage(), {signal});

        if (this.options.showCartButton) {
            this.add2cartLink = JSGallery2.createControl('button', ['eventgallery-add2cart', 'eventgallery-openAdd2cart', 'jsgallery-add2cart', 'jsgallery-chip'], JSGallery2.label('KEY_BUY', 'Buy image'));
            this.add2cartLink.innerHTML = icon('cart-plus');
            this.bigImage.parentElement.appendChild(this.add2cartLink);
            this.dispatchRebindAdd2CartButtons();
        }

        if (this.options.showCartConnector) {
            this.cartConnectorLink = JSGallery2.createControl('a', ['button-cart-connector', 'jsgallery-cartconnector', 'jsgallery-chip'], JSGallery2.label('KEY_CART_CONNECTOR', 'Cart'));
            this.cartConnectorLink.href = '#';
            this.cartConnectorLink.id = 'ajax-cartconnector';
            this.cartConnectorLink.innerHTML = icon('cart');
            this.cartConnectorLink.setAttribute('rel', this.options.cartConnectorLinkRel);
            this.bigImage.parentElement.appendChild(this.cartConnectorLink);
        }

        if (this.options.showSocialMediaButton) {
            this.socialmediabutton = JSGallery2.createControl('button', ['social-share-button', 'jsgallery-socialmedia', 'jsgallery-chip'], JSGallery2.label('KEY_SHARE', 'Share'));
            this.socialmediabutton.id = 'ajax-social-media-button';
            this.socialmediabutton.innerHTML = icon('share');
            this.socialmediabutton.addEventListener('click', e => {
                e.preventDefault();
                let href = this.socialmediabutton.getAttribute('data-href');
                let overlay = new Overlay();
                overlay.openOverlay(href);
            }, {signal});
            this.bigImage.parentElement.appendChild(this.socialmediabutton);
        }

        document.addEventListener('keydown', (e) => this.keyboardHandler(e), {signal});

		let swipeleft =  (e) => {
		    this.nextImage(e);
		};

		let swiperight = (e) => {
		    this.prevImage(e);
		};

        let tabaction = () => {
            this.bigImage.click();
        };

        addTouch(document.querySelector(this.options.touchContainerSelector),
            swiperight,
            swipeleft,
            tabaction,
            null,
        );
    }

    /**
     * Tells the mini cart that a new add2cart button showed up.
     */
    dispatchRebindAdd2CartButtons() {
        document.dispatchEvent(new CustomEvent(Events.rebindAdd2CartButtons, {detail: null}));
    }

    /**
     * Handles the keyboard shortcuts of the gallery: the digits 1-9 go to a page, the arrow
     * keys to the previous and the next image. They act only while the keys mean nothing
     * else on the page: not while the lightbox is open, not for a key which something else
     * handled, not with a modifier held, and never when the visitor types into a field, an
     * editable element or an open dialog - until 7.0.0 the digits and arrows of a login
     * form, a search field or the quantity field of the format dialog paged the gallery.
     *
     * @param {KeyboardEvent} event
     */
    keyboardHandler(event) {
        if (window.Eventgallery.lightbox?.isOpen() === true) {
            return;
        }

        if (!JSGallery2.isKeyForTheGallery(event, this.bigImageContainer)) {
            return;
        }

        const key = event.key;
        // Digits 1-9: jump to page 0-8
        if (/^[1-9]$/.test(key)) {
            event.preventDefault();
            this.gotoPage(parseInt(key, 10) - 1);
            return;
        }

        // Arrow navigation
        if (key === 'ArrowLeft') {
            event.preventDefault();
            this.prevImage(event);
            return;
        }
        if (key === 'ArrowRight') {
            event.preventDefault();
            this.nextImage(event);
        }
    }

    /**
     * Whether a key press is meant for the gallery: not one something else handled already,
     * not one with a modifier, and not one typed into a field, an editable element or an
     * open dialog. A button of the stage itself (the zones, the zoom, the chips) is no field: a
     * click leaves it focused, and the arrows and digits mean nothing on a button.
     *
     * @param {KeyboardEvent} event
     * @param {Element|null} [stage] the container of the big image
     * @returns {boolean}
     */
    static isKeyForTheGallery(event, stage = null) {
        if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) {
            return false;
        }

        const target = event.target;
        if (!(target instanceof Element)) {
            return true;
        }

        if (target.isContentEditable) {
            return false;
        }

        const own = target.closest('input, textarea, select, button, [contenteditable], dialog[open]');
        if (own === null) {
            return true;
        }

        return own.tagName === 'BUTTON' && stage instanceof Element && stage.contains(own)
            && own.closest('dialog[open]') === null;
    }

    /**
     * Re-applies the current selection, e.g. after the image urls have been
     * recalculated for a new container width.
     */
    resetThumbs() {
        this.selectIndex(this.currentIndex, true);
    }

    /**
     *    Adds the border to the thumbs and so on. (conversion of static thumbs)
     */
    convertThumbs() {
        this.thumbs.forEach((thumbContainer, count) => {
            this.convertThumb(thumbContainer, count);
        })
    }

    /**
     * Converts one single thumb.
     * @param {HTMLElement} thumbContainer
     * @param {Number} count
     */
    convertThumb(thumbContainer, count) {

        if (thumbContainer === undefined) {
            return;
        }

        thumbContainer.addEventListener('click', (e) => {
            e.preventDefault();
            this.select(thumbContainer);
        }, {signal: this.abortController.signal});

        // no longer read by this class, but kept for themes and integrations
        // which style or script the thumbnails by their position.
        thumbContainer.setAttribute('data-counter', count);
    }

    /**
     *    Selects a certain image. (You have to pass the outer container of the image)
     *    @param {HTMLElement} container
     *    @param {Boolean} [forceReload]
     *    @return {Boolean} whether the selection was applied
     */
    select(container, forceReload = false) {
        return this.selectIndex(this.thumbs.indexOf(container), forceReload);
    }

    /**
     *    Selects the thumbnail with the given index and derives everything else
     *    from it: the active thumbnail, the visible page, the url, the overlay
     *    buttons and the big image.
     *
     *    @param {Number} index
     *    @param {Boolean} [forceReload]
     *    @return {Boolean} whether the selection was applied
     */
    selectIndex(index, forceReload = false) {
        if (index < 0 || index >= this.thumbs.length) {
            return false;
        }

        //this prevents an ugly effect if you click on the currently selected item
        if (index === this.currentIndex && !forceReload) {
            return false;
        }

        if (this.selectedContainer !== undefined) {
            this.deselect(this.selectedContainer);
        }

        this.currentIndex = index;
        const container = this.thumbs[index];

        this.updateUrl(index);

        //if the target image is not on the current page, we have to go there
        this.showPage(this.pageForIndex(index));

        container.classList.add(this.options.activeClass);

        // the link of the thumbnail: inside its eg-tile, or the first child of an older markup
        let source = container.querySelector('a.event-thumbnail') ?? container.children[0];
        const isVideo = container.getElementsByTagName('A')[0]?.getAttribute('data-type') === 'video';

        this.bigImageContainer.classList.toggle('is-video', isVideo);
        this.zoomLink.innerHTML = JSGallery2.zoneMark(isVideo ? 'play' : 'zoom');

        // prepare the add2cart button
        if (this.options.showCartButton) {
            if (!isVideo && container.getElementsByTagName('A')[0]?.getAttribute('data-type') === 'image') {
                this.add2cartLink.setAttribute('data-id', source.getAttribute('data-id'));
                this.add2cartLink.hidden = false;
            } else {
                // a video is not sold here: the chip goes by hidden, so the stylesheet keeps its look
                this.add2cartLink.hidden = true;
            }
        }

        if (this.options.showCartConnector) {
            this.cartConnectorLink.setAttribute('data-folder', source.getAttribute('data-folder'));
            this.cartConnectorLink.setAttribute('data-file', source.getAttribute('data-file'));
            this.cartConnectorLink.setAttribute('href', decodeURIComponent(source.getAttribute('data-cart-connector-link')));
        }

        if (this.options.showSocialMediaButton) {
            this.socialmediabutton.setAttribute('data-href', decodeURIComponent(source.getAttribute('data-social-sharing-link')) );
        }

        this.dispatchRebindAdd2CartButtons();

        // now lets set the image
        this.setImage(source);

        return true;
    }


    /**
     * Selects an image by its thumbnail index. Indexes outside of the gallery
     * fall back to the first image.
     * @param {Number} index of the thumbnail, starting with 0
     */
    selectByIndex(index) {
        if (index < 0 || this.thumbs.length <= index) {
            index = 0;
        }
        this.selectIndex(index);
    }

    /**
     * Writes the selected image into the url so it can be linked to. This
     * replaces the current history entry on purpose - pushing one per image
     * would fill up the history and break the back button.
     * @param {Number} index
     */
    updateUrl(index) {
        if (!history || !history.replaceState) {
            return;
        }
        history.replaceState(history.state, '', Helpers.addUrlHashParameter(window.location.href, 'imageno', index));
    }

    /**
     * Applies the image referenced by the url, e.g. after the user navigated
     * back and forth or followed a deep link on the same page.
     */
    selectFromUrl() {
        const index = parseInt(Helpers.getUrlHashParameterValue(document.location.href, 'imageno'));
        if (!isNaN(index) && index !== this.currentIndex) {
            this.selectByIndex(index);
        }
    }

    /**
     *    Opposite to method above.
     *    @param {HTMLElement} container
     */
    deselect(container) {
        container.classList.remove(this.options.activeClass);
    }

    bigImageClickHandler() {
        this.createGallerySlides();
        window.Eventgallery.lightbox.openPhotoSwipe(
            this.currentIndex, this.slides, true, false, false
        ).catch((error) => console.error('The lightbox could not load.', error));
        window.Eventgallery.lightbox.setAfterChangeEventListener(() => {
            let gid = window.Eventgallery.lightbox.getCurrentSlide().gid;
            if (this.slides.length>0 && gid === this.slides[0].gid) {
                this.selectByIndex(window.Eventgallery.lightbox.getCurrentIndex());
            }
        });
    }
    /**
     *    Changes the full size image to given one.
     *    @param lightboxLinkElement
     */
    setImage(lightboxLinkElement) {

            this.bigImage.style.opacity = '0.1';
            let newSrc = lightboxLinkElement.getAttribute('rel');
            let title =  decodeURIComponent(lightboxLinkElement.getAttribute('data-description'));

            if (window.Eventgallery.lightbox?.isOpen() === true) {
               window.Eventgallery.lightbox.gotoSlide(this.currentIndex);
            }

            const titleTarget = this.options.titleTarget ? this.galleryElement.querySelector(this.options.titleTarget) : null;
            if (titleTarget) {
                titleTarget.innerHTML = title ?? '';
            }

            this.bigImage.setAttribute('src', newSrc);
            // the stage is named like the thumbnail it shows
            this.bigImage.setAttribute('alt', JSGallery2.altOf(lightboxLinkElement));
            this.bigImageBackdrop.setAttribute('src', newSrc);
    }

    /**
     * @param lightboxLinkElement the anchor of a thumbnail
     * @returns {string} the alt of its image, '' without one
     */
    /**
     * A control of the stage: a button (or a link) with its name as aria-label and title, so a
     * control which shows an icon alone - or nothing until it is hovered - is announced by name.
     *
     * @param {'button'|'a'} tag
     * @param {string[]} classes
     * @param {string} name
     * @returns {HTMLElement}
     */
    /**
     * The mark in the middle of a zone over the stage: an icon of the sprite, drawn like the
     * chips in the corners (ajax.less). The zone carries the name, so the icon has none.
     *
     * @param {string} name the name of the glyph
     * @returns {string}
     */
    static zoneMark(name) {
        return '<span class="jsgallery-zone-mark">' + icon(name) + '</span>';
    }

    static createControl(tag, classes, name) {
        const control = document.createElement(tag);
        if (tag === 'button') {
            control.type = 'button';
        }
        control.classList.add(...classes);
        control.setAttribute('aria-label', name);
        control.setAttribute('title', name);
        return control;
    }

    /**
     * A text of the lightbox configuration the page prints (MediaLoader), with an English
     * fallback for a page without it.
     *
     * @param {string} key
     * @param {string} fallback
     * @returns {string}
     */
    static label(key, fallback) {
        const configuration = window.EventGalleryLightboxConfiguration;
        return configuration && configuration[key] ? String(configuration[key]) : fallback;
    }

    static altOf(lightboxLinkElement) {
        const image = lightboxLinkElement.querySelector('img');
        return image ? (image.getAttribute('alt') || '') : '';
    }

    showBigImage() {
        this.image.style.opacity = 1;
        this.bigImage.style.opacity = 1;
    }

    /**
     *    Selects the previous image.
     */
    prevImage(e) {
        if (e !== undefined) {
            e.preventDefault();
        }
        this.selectByIndex(this.currentIndex - 1);
    }
    /**
     *    Selects the next image.
     */
    nextImage(e) {
        if (e !== undefined) {
            e.preventDefault();
        }
        this.selectByIndex(this.currentIndex + 1);
    }

    /**
     * Slides the given page into view and updates the paging handles. This does
     * not touch the selection - selectIndex() calls it to follow the selected
     * thumbnail.
     *
     * Every page is exactly as wide as the track (see ajax.less), so shifting
     * the track by whole percentages moves it by whole pages - independent of
     * the actual pixel width.
     *
     * @param {Number} pageNumber
     * @return {Boolean} whether the page is now visible
     */
    showPage(pageNumber) {
        if (pageNumber < 0 || pageNumber >= this.lastPage) {
            return false;
        }

        if (pageNumber === this.currentPageNumber) {
            return true;
        }

        this.currentPageNumber = pageNumber;
        // one page plus the gutter between two pages, see --eg-page-gap in ajax.less; the pages
        // follow the reading direction, so on a right-to-left page the next one lies to the left
        // and the track moves to the right (until 7.0.0 it moved to the left there and showed
        // nothing from the second page on)
        const shift = window.getComputedStyle(this.pageContainer).direction === 'rtl'
            ? '(100% + var(--eg-page-gap, 0px))'
            : '(-100% - var(--eg-page-gap, 0px))';
        this.pageContainer.style.transform = 'translateX(calc(' + shift + ' * ' + pageNumber + '))';
        this.updateHandles();

        return true;
    }

    /**
     * Navigates to given page and selects the first image of it.
     * Also hides the handles (if set).
     * @param pageNumber {Number}
     */
    gotoPage(pageNumber) {
        if (!this.showPage(pageNumber)) {
            return;
        }

        this.selectIndex(this.firstIndexOfPage(pageNumber));
    }

    updateHandles() {
        //update handles

        if (this.options.prevHandle) {
            this.options.prevHandle.style.opacity = this.currentPageNumber === 0 ? 0 : 1;
        }
        if (this.options.nextHandle) {
            this.options.nextHandle.style.opacity = this.currentPageNumber === this.lastPage - 1 ? 0 : 1;
        }

        if (this.options.countHandle) {
            this.updatePagingBar(this.currentPageNumber, this.lastPage);
        }

    }

    updatePagingBar(currentPage, pageCount) {
        if (pageCount <= 1 ) {
            return;
        }

        //init the pagingbar
        if (this.countLinks.length === 0) {
            for (let i = 0; i < pageCount; i++) {
                this.createCountLink(i);
            }
        }

        if (pageCount > 9) {

            this.countLinks.forEach((item) => { item.hidden = false; });

            let skipFromRight = pageCount;
            let skipFromLeft = 0;

            let spaceToRight = pageCount - currentPage - 1;
            let spaceToLeft = currentPage;

            if (spaceToLeft > 4 && spaceToRight > 4) {
                skipFromLeft = currentPage - 4;
                skipFromRight = currentPage + 5;
            } else {
                if (spaceToLeft <= 4) {
                    skipFromLeft = 0;
                    skipFromRight = currentPage + 5 + (4 - spaceToLeft);
                }
                if (spaceToRight <= 4) {
                    skipFromLeft = currentPage - 4 - (4 - spaceToRight);
                    skipFromRight = pageCount;
                }
            }

            for (let i = 0; i < skipFromLeft; i++) {
                this.countLinks[i].hidden = true;
            }

            for (let i = skipFromRight; i < pageCount; i++) {
                this.countLinks[i].hidden = true;
            }
        }

        // the current page: "active" on the item, aria-current on the link of the eg-pagination
        this.countLinks.forEach((item) => {
            item.classList.remove('active');
            item.firstElementChild?.removeAttribute('aria-current');
        });
        this.countLinks[currentPage].classList.add('active');
        this.countLinks[currentPage].firstElementChild?.setAttribute('aria-current', 'page');

    }

    createCountLink(currentPageNumber) {

        let myAnchor = document.createElement('A');
        myAnchor.href = '#';
        myAnchor.classList.add('eg-btn', 'eg-btn--quiet', 'eg-btn--sm', 'eg-pagination__link');
        myAnchor.textContent = currentPageNumber + 1;

        myAnchor.addEventListener('click',(e) => {
            e.preventDefault();
            this.gotoPage(currentPageNumber);
        }, {signal: this.abortController.signal});

        let myListItem = document.createElement('LI');
        myListItem.classList.add('count');
        myListItem.classList.add('eg-pagination__number');
        myListItem.id = 'count' + currentPageNumber;

        myListItem.appendChild(myAnchor);

        this.countLinks.push(myListItem);
        this.options.countHandle.appendChild(myListItem);
    }
}
